import { ReactNode } from "react";
import styled from "styled-components";
import { startOfDay } from "date-fns";
import { ButtonBase } from "./Button";
import { designSystemVars as vars } from "../tokens";
import nextIcon from "../../assets/icons/scheduling-next.svg";
import nextDisabledIcon from "../../assets/icons/scheduling-next-disabled.svg";

const CalendarSurface = styled.div`
  width: 320px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: ${vars.spacing.lg};

  border: none;
  border-radius: ${vars.radius.sm};
  &.react-datepicker {
    background: ${vars.color.background.subtle};
  }
  font-family: ${vars.typography.family.sans};

  box-shadow: ${vars.shadow.dropdown};

  max-height: var(--interview-calendar-max-height, calc(100vh - 32px));
  overflow-y: auto;

  .react-datepicker__children-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: ${vars.spacing.md} 0 0;
    padding: 0;
    gap: ${vars.spacing.md};
    text-align: left;
  }

  .react-datepicker__month-container {
    width: 100%;
    float: none;
    background: ${vars.color.background.transparent};
  }

  .react-datepicker__header {
    padding: 0;
    border: none;
    background: ${vars.color.background.transparent};
  }

  .react-datepicker__month {
    width: 100%;
    margin: 0;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    width: 100%;
  }

  .react-datepicker__day-names {
    margin: ${vars.spacing.md} 0 0;
  }

  .react-datepicker__day-name {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 32px;
    margin: 0;

    color: ${vars.color.text.slate};
    font-family: ${vars.typography.family.sans};
    font-size: ${vars.typography.size.px14};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.sm};
  }

  .react-datepicker__day {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 40px;
    margin: 0;
    box-sizing: border-box;

    color: ${vars.color.text.body};
    background: ${vars.color.background.surface};
    font-family: ${vars.typography.family.sans};
    font-size: ${vars.typography.size.sm};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.px20};
    border-radius: ${vars.radius.xs};
    cursor: default;
  }

  .react-datepicker__day:hover {
    background: ${vars.color.background.hover};
    color: ${vars.color.text.primary};
  }

  .react-datepicker__day--outside-month {
    color: ${vars.color.text.muted};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--selected:hover {
    background: ${vars.color.action.primary};
    color: ${vars.color.background.surface};
    font-weight: ${vars.typography.weight.semibold};
  }

  .react-datepicker__day--today:not(.react-datepicker__day--selected) {
    background: ${vars.color.border.muted};
    color: ${vars.color.text.blackMuted};
    font-weight: ${vars.typography.weight.bold};
  }

  .react-datepicker__day--disabled,
  .react-datepicker__day--disabled.react-datepicker__day--today {
    background: ${vars.color.action.disabled};
    color: ${vars.color.text.muted};
    cursor: not-allowed;
    border-radius: ${vars.radius.none};
    font-weight: ${vars.typography.weight.regular};
  }

  .react-datepicker__day--disabled:hover {
    background: ${vars.color.action.disabled};
    color: ${vars.color.text.muted};
  }

  .react-datepicker__day--disabled.react-datepicker__day--selected,
  .react-datepicker__day--disabled.react-datepicker__day--selected:hover {
    background: ${vars.color.background.selected};
    color: ${vars.color.text.primary};
    border-radius: ${vars.radius.xs};
    font-weight: ${vars.typography.weight.semibold};
  }

  .react-datepicker__day.interview-calendar-day--past:not(
      .react-datepicker__day--selected
    ),
  .react-datepicker__day.interview-calendar-day--past:not(
      .react-datepicker__day--selected
    ):hover {
    color: ${vars.color.text.slate};
  }

  .react-datepicker__triangle,
  .react-datepicker__aria-live {
    display: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 32px;
`;

const Month = styled.div<{ $locked?: boolean }>`
  color: ${({ $locked }) =>
    $locked ? vars.color.text.slate : vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.sm};
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  gap: ${vars.spacing.xl};
`;

const ArrowButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 0;
  &:disabled {
    cursor: not-allowed;
  }
  &:focus-visible {
    box-shadow: ${vars.shadow.focus};
  }
`;

export function CalendarContainer({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  return <CalendarSurface className={className}>{children}</CalendarSurface>;
}

export function CalendarMonthHeader({
  date,
  decreaseMonth,
  increaseMonth,
  disabled = false,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  disabled?: boolean;
}) {
  return (
    <Header>
      <Month $locked={disabled}>
        {date.toLocaleString("en-US", { month: "long", year: "numeric" })}
      </Month>
      <Navigation>
        <ArrowButton
          type="button"
          onClick={decreaseMonth}
          disabled={disabled}
          aria-label="Previous month"
        >
          <img
            src={disabled ? nextDisabledIcon : nextIcon}
            alt=""
            style={{ transform: "rotate(180deg)" }}
          />
        </ArrowButton>
        <ArrowButton
          type="button"
          onClick={increaseMonth}
          disabled={disabled}
          aria-label="Next month"
        >
          <img src={disabled ? nextDisabledIcon : nextIcon} alt="" />
        </ArrowButton>
      </Navigation>
    </Header>
  );
}

export const calendarDayClassName = (day: Date) =>
  day.getTime() < startOfDay(new Date()).getTime()
    ? "interview-calendar-day--past"
    : "";
